import { Dispatch, FormEvent, SetStateAction } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Icon } from '@/lib/icons';
import {
  AuthPasswordToggle,
  AuthSpinner,
  AUTH_FIELD_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
  AUTH_SECONDARY_BUTTON_CLASS,
} from '@/features/auth-shell';
import { ResetPasswordCopy } from './reset-password.types';
import { ResetPasswordCard } from './ResetPasswordLayout';

type ResetPasswordFormPanelProps = {
  t: ResetPasswordCopy;
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  loading: boolean;
  passwordHasMinLength: boolean;
  passwordHasLetterCase: boolean;
  passwordHasDigitAndSpecial: boolean;
  passwordsMatch: boolean;
  canSubmit: boolean;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  setShowPassword: Dispatch<SetStateAction<boolean>>;
  setShowConfirmPassword: Dispatch<SetStateAction<boolean>>;
  onSubmit: (event: FormEvent) => void;
  onBackToLogin: () => void;
};

export function ResetPasswordFormPanel({
  t,
  password,
  confirmPassword,
  showPassword,
  showConfirmPassword,
  loading,
  passwordHasMinLength,
  passwordHasLetterCase,
  passwordHasDigitAndSpecial,
  passwordsMatch,
  canSubmit,
  onPasswordChange,
  onConfirmPasswordChange,
  setShowPassword,
  setShowConfirmPassword,
  onSubmit,
  onBackToLogin,
}: ResetPasswordFormPanelProps) {
  return (
    <ResetPasswordCard title={t.resetPasswordNewTitle} description={t.resetPasswordNewDesc} t={t} eyebrow={t.resetPasswordFormTitle}>
      <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
        <PasswordField
          id="password"
          label={t.resetPasswordNewLabel}
          value={password}
          visible={showPassword}
          onChange={onPasswordChange}
          onToggle={() => setShowPassword((value) => !value)}
          autoComplete="new-password"
        />

        <PasswordField
          id="confirmPassword"
          label={t.resetPasswordConfirmLabel}
          value={confirmPassword}
          visible={showConfirmPassword}
          onChange={onConfirmPasswordChange}
          onToggle={() => setShowConfirmPassword((value) => !value)}
          autoComplete="new-password"
        />

        <PasswordCriteria
          t={t}
          passwordHasMinLength={passwordHasMinLength}
          passwordHasLetterCase={passwordHasLetterCase}
          passwordHasDigitAndSpecial={passwordHasDigitAndSpecial}
          passwordsMatch={passwordsMatch}
        />

        <div className="grid gap-3 pt-1">
          <Button type="submit" disabled={!canSubmit} className={AUTH_PRIMARY_BUTTON_CLASS}>
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <AuthSpinner />
                <span>{t.resetPasswordLoading}</span>
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <span>{t.resetPasswordSubmit}</span>
                <Icon name="check" className="h-4 w-4" />
              </span>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={onBackToLogin}
            className={`${AUTH_SECONDARY_BUTTON_CLASS} w-full`}
          >
            <Icon name="arrowLeft" className="mr-2 h-4 w-4" />
            {t.backToLogin}
          </Button>
        </div>
      </form>
    </ResetPasswordCard>
  );
}

function PasswordField({
  id,
  label,
  value,
  visible,
  autoComplete,
  onChange,
  onToggle,
}: {
  id: string;
  label: string;
  value: string;
  visible: boolean;
  autoComplete: string;
  onChange: (value: string) => void;
  onToggle: () => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
      </Label>

      <div className="relative">
        <Icon name="lock" className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          placeholder="••••••••"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required
          minLength={8}
          className={`pl-10 pr-11 ${AUTH_FIELD_CLASS}`}
          autoComplete={autoComplete}
        />
        <AuthPasswordToggle visible={visible} onToggle={onToggle} />
      </div>
    </div>
  );
}

function PasswordCriteria({
  t,
  passwordHasMinLength,
  passwordHasLetterCase,
  passwordHasDigitAndSpecial,
  passwordsMatch,
}: {
  t: ResetPasswordCopy;
  passwordHasMinLength: boolean;
  passwordHasLetterCase: boolean;
  passwordHasDigitAndSpecial: boolean;
  passwordsMatch: boolean;
}) {
  return (
    <div className="space-y-2.5 rounded-2xl border border-border/70 bg-muted/30 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {t.resetPasswordCriteria}
      </p>

      <ul className="grid gap-2 text-xs sm:grid-cols-2" aria-live="polite">
        <Criterion checked={passwordHasMinLength} label={t.resetPasswordAtLeast6} />
        <Criterion checked={passwordHasLetterCase} label={t.resetPasswordUpperLower} />
        <Criterion checked={passwordHasDigitAndSpecial} label={t.resetPasswordDigitSpecial} />
        <Criterion checked={passwordsMatch} label={t.resetPasswordMatch} />
      </ul>
    </div>
  );
}

function Criterion({ checked, label }: { checked: boolean; label: string }) {
  return (
    <li className={`flex items-center gap-2 transition-colors ${checked ? 'text-success' : 'text-muted-foreground'}`}>
      <Icon name={checked ? 'check' : 'x'} className="h-3.5 w-3.5 shrink-0" />
      <span>{label}</span>
    </li>
  );
}

